import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Blocks in-app navigation and browser close/refresh when `shouldBlock` is true.
 * Works with legacy `<BrowserRouter>` (no data router required).
 *
 * Intercepts:
 *  - Browser back/forward (popstate)
 *  - Programmatic navigation (history.pushState / replaceState)
 *  - Browser tab close / refresh (beforeunload)
 */
export function useUnsavedChangesGuard(shouldBlock: boolean) {
  const location = useLocation();
  const blockRef = useRef(shouldBlock);
  blockRef.current = shouldBlock;

  const locationRef = useRef(location.pathname);
  locationRef.current = location.pathname;

  // Track whether we're currently executing an allowed navigation so our
  // monkey-patched pushState/replaceState don't re-prompt.
  const allowingRef = useRef(false);

  // Browser tab close / refresh
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (blockRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  // Intercept popstate (browser back/forward)
  useEffect(() => {
    const handler = () => {
      if (!blockRef.current) return;

      if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
        // User confirmed — let the navigation happen naturally. The router
        // will pick up the new URL from popstate and render accordingly.
        return;
      }

      // User cancelled — push the current location back to undo the pop.
      // We use allowingRef so our pushState patch doesn't re-prompt.
      allowingRef.current = true;
      window.history.pushState(null, '', locationRef.current);
      allowingRef.current = false;
    };

    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, []);

  // Intercept programmatic navigation (history.pushState / replaceState)
  // This catches React Router's navigate(), <Link> clicks, sidebar buttons, etc.
  useEffect(() => {
    const originalPush = window.history.pushState.bind(window.history);
    const originalReplace = window.history.replaceState.bind(window.history);

    const intercept = (
      // Underscored rather than removed: `intercept` is handed to
      // history.pushState/replaceState, so the parameter has to stay in
      // position even though this implementation ignores the original.
      _original: typeof window.history.pushState,
      args: Parameters<typeof window.history.pushState>,
    ): boolean => {
      if (allowingRef.current || !blockRef.current) return false;

      const url = args[2];
      if (url != null && String(url) !== locationRef.current) {
        if (!window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
          return true; // blocked
        }
      }
      return false;
    };

    window.history.pushState = function (...args) {
      if (intercept(originalPush, args)) return;
      return originalPush(...args);
    };

    window.history.replaceState = function (...args) {
      if (intercept(originalReplace, args)) return;
      return originalReplace(...args);
    };

    return () => {
      window.history.pushState = originalPush;
      window.history.replaceState = originalReplace;
    };
  }, []);
}
